import { useEffect, useMemo, useReducer, useRef, useState } from "react";
import packageMetadata from "../package.json";
import { getAvailableUserPlaceableComponents, getComponent } from "./data/catalog";
import { getMap, getMaps, getRack } from "./data/maps";
import { getDefaultScenarioId, getScenario, getScenarios } from "./data/scenarios";
import { getAvailabilityZoneCapacity, getDatacenterCapacity, getRackCapacity } from "./domain/capacity";
import { restoreLocalPlan, saveLocalPlan } from "./domain/persistence";
import {
    findHighestFreeStartU,
    isComponentCompatibleWithRack,
    rackContentsFitSize,
    validatePlacement,
} from "./domain/placement";
import {
    applyRackSettingsToMap,
    findRackSettings,
    getRackDisplayName,
    isRackSizeConfigurable,
} from "./domain/rack-settings";
import type { MapId, Placement, RackSettings, ScenarioId } from "./domain/types";
import { getSafeFilename, importPlanJson, serializePlan } from "./domain/utp";
import { LanguageContext, translate, translateEntity, translateRuntimeText, useUiLanguage } from "./i18n";
import { INITIAL_STATE, plannerReducer } from "./store/planner-store";
import { ApiImportModal } from "./ui/ApiImportModal";
import { CapacitySummary } from "./ui/CapacitySummary";
import { ComponentCatalog } from "./ui/ComponentCatalog";
import { Floorplan } from "./ui/Floorplan";
import { DownloadIcon, FolderIcon, PlusIcon, RackIcon, UploadIcon } from "./ui/icons";
import { Modal } from "./ui/Modal";
import { RackDesigner } from "./ui/RackDesigner";
import { SavegameImportModal } from "./ui/SavegameImportModal";
import { SearchSelect } from "./ui/SearchSelect";

type ModalState =
    | { readonly type: "project" }
    | { readonly type: "new-plan" }
    | { readonly type: "rename-plan" }
    | { readonly type: "import-savegame"; readonly file: File }
    | { readonly type: "import-api" }
    | { readonly type: "configure-rack"; readonly rackId: string }
    | { readonly type: "place"; readonly componentId?: string }
    | { readonly type: "move-placement"; readonly placement: Placement }
    | { readonly type: "rename-placement"; readonly placement: Placement }
    | { readonly type: "delete-placements"; readonly placementIds: readonly string[] }
    | null;

const DATACENTER_CAPACITY_SCOPE = "datacenter";
const AVAILABILITY_ZONE_SCOPE_PREFIX = "zone:";
const RACK_SIZE_OPTIONS = [24, 42] as const;

function restoreInitialState(): typeof INITIAL_STATE {
    const plan = restoreLocalPlan(window.localStorage);
    if (plan === null) {
        return INITIAL_STATE;
    }
    return {
        ...INITIAL_STATE,
        plan,
        selectedRackId: getMap(plan.project.mapId)?.racks[0]?.id ?? null,
        view: "floorplan",
        notice: "Last browser workspace restored.",
    };
}

function downloadPlan(name: string, content: string): void {
    const blob = new Blob([content], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = getSafeFilename(name);
    anchor.click();
    URL.revokeObjectURL(url);
}

function EmptyWorkspace({
    onCreate,
    onImport,
    onImportSavegame,
    onImportApi,
}: {
    readonly onCreate: () => void;
    readonly onImport: () => void;
    readonly onImportSavegame: () => void;
    readonly onImportApi: () => void;
}): React.JSX.Element {
    const { t } = useUiLanguage();
    return (
        <section className="empty-workspace">
            <div className="empty-mark">
                <RackIcon />
            </div>
            <span className="eyebrow">{t("localWorkspace")}</span>
            <h1>{t("emptyTitle")}</h1>
            <p>{t("emptyIntro")}</p>
            <div className="empty-actions">
                <button className="button button-primary button-large" type="button" onClick={onCreate}>
                    <PlusIcon />
                    {t("newPlan")}
                </button>
                <button className="button button-large" type="button" onClick={onImport}>
                    <UploadIcon />
                    {t("importPlan")}
                </button>
                <button className="button button-large" type="button" onClick={onImportSavegame}>
                    <UploadIcon />
                    <span className="savegame-import-label">
                        {t("importSavegame")}
                        <span className="savegame-beta-badge">{t("beta")}</span>
                    </span>
                </button>
                <button className="button button-large" type="button" onClick={onImportApi}>
                    <UploadIcon />
                    <span className="savegame-import-label">
                        {t("importFromApi")}
                        <span className="savegame-beta-badge">{t("beta")}</span>
                    </span>
                </button>
            </div>
            <div className="empty-features">
                <span>
                    <i>01</i>
                    {t("featurePositions")}
                </span>
                <span>
                    <i>02</i>
                    {t("featureSafety")}
                </span>
                <span>
                    <i>03</i>
                    {t("featureStorage")}
                </span>
            </div>
        </section>
    );
}

interface ProjectViewProps {
    readonly onNew: () => void;
    readonly onImport: () => void;
    readonly onImportSavegame: () => void;
    readonly onImportApi: () => void;
    readonly onExport: () => void;
    readonly onRename: () => void;
    readonly onReset: () => void;
    readonly onScenarioChange: (scenarioId: ScenarioId) => void;
    readonly planName: string;
    readonly mapId: MapId;
    readonly mapName: string;
    readonly scenarioId: ScenarioId;
    readonly createdAt: string;
    readonly updatedAt: string;
    readonly rackCount: number;
    readonly totalRackU: number;
    readonly placementCount: number;
}

function ProjectView(props: ProjectViewProps): React.JSX.Element {
    const { entity, t } = useUiLanguage();
    const scenarioOptions = getScenarios(props.mapId).map((scenario) => ({
        value: scenario.id,
        label: entity("scenario", scenario.id, scenario.name),
        description: t("scenarioAvailabilityHint"),
    }));
    return (
        <section className="project-manager">
            <div className="project-manager-layout">
                <aside className="project-summary">
                    <header className="project-overview">
                        <span className="project-file-mark">{t("fileTypeMark")}</span>
                        <div className="project-overview-copy">
                            <span className="eyebrow">{t("activePlan")}</span>
                            <h2>{props.planName}</h2>
                            <p>{getSafeFilename(props.planName)}</p>
                        </div>
                    </header>
                    <span className="status-pill project-autosave-status">
                        <i />
                        {t("autosaved")}
                    </span>
                    <dl className="project-facts">
                        <div>
                            <dt>{t("officialMap")}</dt>
                            <dd>{props.mapName}</dd>
                        </div>
                        <div className="project-scenario-setting">
                            <dt>{t("scenario")}</dt>
                            <dd>
                                <SearchSelect
                                    compact
                                    ariaLabel={t("changeScenario")}
                                    value={props.scenarioId}
                                    options={scenarioOptions}
                                    searchPlaceholder={t("searchScenarios")}
                                    onChange={(value) => {
                                        const scenario = getScenario(value);
                                        if (scenario?.mapIds.includes(props.mapId)) {
                                            props.onScenarioChange(scenario.id);
                                        }
                                    }}
                                />
                            </dd>
                            <small className="project-scenario-hint">{t("changeScenarioHint")}</small>
                        </div>
                        <div>
                            <dt>{t("rackCapacity")}</dt>
                            <dd>
                                {props.rackCount} {t("racks")} · {props.totalRackU}U
                            </dd>
                        </div>
                        <div>
                            <dt>{t("userEquipment")}</dt>
                            <dd>
                                {props.placementCount} {t("components")}
                            </dd>
                        </div>
                        <div>
                            <dt>{t("created")}</dt>
                            <dd>{new Date(props.createdAt).toLocaleDateString("en-GB")}</dd>
                        </div>
                        <div>
                            <dt>{t("lastChange")}</dt>
                            <dd>{new Date(props.updatedAt).toLocaleString("en-GB")}</dd>
                        </div>
                    </dl>
                </aside>
                <div className="project-manager-main">
                    <section className="project-manager-section is-new-project">
                        <header>
                            <h3>{t("newProjectActions")}</h3>
                            <p>{t("newProjectActionsHint")}</p>
                        </header>
                        <div className="project-command-grid">
                            <button type="button" onClick={props.onNew}>
                                <PlusIcon />
                                <span className="project-action-copy">
                                    <strong>{t("newPlan")}</strong>
                                    <small>{t("chooseMap")}</small>
                                </span>
                            </button>
                            <button type="button" onClick={props.onImport}>
                                <UploadIcon />
                                <span className="project-action-copy">
                                    <strong>{t("importPlan")}</strong>
                                    <small>{t("validateOpen")}</small>
                                </span>
                            </button>
                            <button type="button" onClick={props.onImportSavegame}>
                                <UploadIcon />
                                <span className="project-action-copy">
                                    <strong className="savegame-import-label">
                                        {t("importSavegame")}
                                        <span className="savegame-beta-badge">{t("beta")}</span>
                                    </strong>
                                    <small>{t("importSavegameActionHint")}</small>
                                </span>
                            </button>
                            <button type="button" onClick={props.onImportApi}>
                                <UploadIcon />
                                <span className="project-action-copy">
                                    <strong className="savegame-import-label">
                                        {t("importFromApi")}
                                        <span className="savegame-beta-badge">{t("beta")}</span>
                                    </strong>
                                    <small>{t("importFromApiActionHint")}</small>
                                </span>
                            </button>
                        </div>
                    </section>
                    <section className="project-manager-section is-current-project">
                        <header>
                            <h3>{t("currentProjectActions")}</h3>
                            <p>{t("currentProjectActionsHint")}</p>
                        </header>
                        <div className="project-command-grid">
                            <button type="button" onClick={props.onRename}>
                                <FolderIcon />
                                <span className="project-action-copy">
                                    <strong>{t("renamePlan")}</strong>
                                    <small>{t("updateTitle")}</small>
                                </span>
                            </button>
                            <button type="button" onClick={props.onExport}>
                                <DownloadIcon />
                                <span className="project-action-copy">
                                    <strong>{t("exportPlan")}</strong>
                                    <small>{t("downloadPortable")}</small>
                                </span>
                            </button>
                        </div>
                    </section>
                    <section className="project-reset-row">
                        <div>
                            <h3>{t("resetPlacements")}</h3>
                            <p>{t("fixedProtected")}</p>
                        </div>
                        <button className="button button-danger" type="button" onClick={props.onReset}>
                            {t("resetPlan")}
                        </button>
                    </section>
                </div>
            </div>
        </section>
    );
}

interface PlacementDialogProps {
    readonly mapId: MapId;
    readonly scenarioId: ScenarioId;
    readonly selectedRackId: string | null;
    readonly initialComponentId?: string;
    readonly placements: readonly Placement[];
    readonly rackSettings: readonly RackSettings[];
    readonly onCancel: () => void;
    readonly onPlace: (componentId: string, rackId: string, startU: number, localName?: string) => void;
}

function PlacementDialog({
    mapId,
    scenarioId,
    selectedRackId,
    initialComponentId,
    placements,
    rackSettings,
    onCancel,
    onPlace,
}: PlacementDialogProps): React.JSX.Element {
    const { entity, runtimeText, t } = useUiLanguage();
    const baseMap = getMap(mapId);
    const map = baseMap === undefined ? undefined : applyRackSettingsToMap(baseMap, rackSettings);
    const userPlaceableComponents = getAvailableUserPlaceableComponents(scenarioId, mapId);
    const userPlaceableCategories = [...new Set(userPlaceableComponents.map((component) => component.category))];
    const initialComponent = userPlaceableComponents.find((component) => component.id === initialComponentId);
    const [componentId, setComponentId] = useState(initialComponent?.id ?? userPlaceableComponents[0]?.id ?? "");
    const initialCategory = initialComponent?.category ?? "All";
    const [category, setCategory] = useState(initialCategory);
    const [rackId, setRackId] = useState(selectedRackId ?? map?.racks[0]?.id ?? "");
    const [startU, setStartU] = useState(1);
    const [localName, setLocalName] = useState("");
    const rack = map === undefined ? undefined : getRack(map, rackId);
    const compatibleComponents = userPlaceableComponents.filter(
        (definition) => rack !== undefined && isComponentCompatibleWithRack(rack, definition),
    );
    const compatibleCategories = userPlaceableCategories.filter((item) =>
        compatibleComponents.some((definition) => definition.category === item),
    );
    const result =
        map === undefined
            ? { valid: false, reason: "Map unavailable." }
            : validatePlacement(map, placements, componentId, rackId, startU);
    const component = getComponent(componentId);
    const availableComponents = compatibleComponents.filter(
        (definition) => category === "All" || definition.category === category,
    );

    function changeCategory(nextCategory: string): void {
        setCategory(nextCategory);
        const current = getComponent(componentId);
        if (nextCategory === "All" || current?.category === nextCategory) {
            return;
        }
        const firstMatch = compatibleComponents.find((definition) => definition.category === nextCategory);
        if (firstMatch !== undefined) {
            setComponentId(firstMatch.id);
        }
    }

    function changeRack(nextRackId: string): void {
        setRackId(nextRackId);
        const nextRack = map === undefined ? undefined : getRack(map, nextRackId);
        const current = getComponent(componentId);
        if (nextRack === undefined || (current !== undefined && isComponentCompatibleWithRack(nextRack, current))) {
            return;
        }
        const firstMatch = userPlaceableComponents.find((definition) =>
            isComponentCompatibleWithRack(nextRack, definition),
        );
        if (firstMatch !== undefined) {
            setComponentId(firstMatch.id);
            setCategory(firstMatch.category);
        }
    }

    return (
        <Modal
            title={t("placeComponent")}
            subtitle={t("placeSubtitle")}
            onClose={onCancel}
            footer={
                <>
                    <button className="button" type="button" onClick={onCancel}>
                        {t("cancel")}
                    </button>
                    <button
                        className="button button-primary"
                        type="button"
                        disabled={!result.valid}
                        onClick={() => onPlace(componentId, rackId, startU, localName.trim() || undefined)}
                    >
                        {t("placeAt")} U{startU}
                    </button>
                </>
            }
        >
            <div className="form-grid">
                <div className="form-field">
                    <span>{t("category")}</span>
                    <SearchSelect
                        ariaLabel={t("category")}
                        value={category}
                        options={["All", ...compatibleCategories].map((item) => ({
                            value: item,
                            label: item === "All" ? t("allComponents") : entity("category", item, item),
                        }))}
                        onChange={changeCategory}
                        searchPlaceholder={t("searchCategories")}
                    />
                </div>
                <div className="form-field">
                    <span>{t("officialComponent")}</span>
                    <SearchSelect
                        ariaLabel={t("officialComponent")}
                        value={componentId}
                        options={availableComponents.map((item) => ({
                            value: item.id,
                            label: entity("component", item.id, item.name),
                            description: `${entity("category", item.category, item.category)} · ${item.sizeU}U`,
                        }))}
                        onChange={setComponentId}
                        searchPlaceholder={t("searchOfficialComponents")}
                    />
                </div>
                <div className="form-field">
                    <span>{t("targetRack")}</span>
                    <SearchSelect
                        ariaLabel={t("targetRack")}
                        value={rackId}
                        options={(map?.racks ?? []).map((item) => ({
                            value: item.id,
                            label: getRackDisplayName(item, rackSettings, entity("rack", item.id, item.name)),
                            description: `${item.sizeU}U`,
                        }))}
                        onChange={changeRack}
                        searchPlaceholder={t("searchRacks")}
                    />
                </div>
                <label>
                    <span>{t("startRackUnit")}</span>
                    <input
                        type="number"
                        min="1"
                        max={rack?.sizeU ?? 42}
                        value={startU}
                        onChange={(event) => setStartU(Number(event.target.value))}
                    />
                </label>
                <label>
                    <span>
                        {t("localName")} <small>{t("optional")}</small>
                    </span>
                    <input
                        maxLength={80}
                        value={localName}
                        onChange={(event) => setLocalName(event.target.value)}
                        placeholder={t("localNamePlaceholder")}
                    />
                </label>
            </div>
            <div className={`placement-feedback ${result.valid ? "is-valid" : "is-invalid"}`}>
                <strong>{result.valid ? t("positionAvailable") : t("cannotPlace")}</strong>
                <span>
                    {result.valid
                        ? `${component?.sizeU ?? 0}U · U${startU}–U${startU + (component?.sizeU ?? 1) - 1} · ${
                              rack === undefined
                                  ? t("thisRack")
                                  : getRackDisplayName(rack, rackSettings, entity("rack", rack.id, rack.name))
                          }`
                        : runtimeText(result.reason)}
                </span>
            </div>
        </Modal>
    );
}

interface MoveDialogProps {
    readonly mapId: MapId;
    readonly placement: Placement;
    readonly placements: readonly Placement[];
    readonly rackSettings: readonly RackSettings[];
    readonly onCancel: () => void;
    readonly onMove: (rackId: string, startU: number) => void;
}

function MoveDialog({
    mapId,
    placement,
    placements,
    rackSettings,
    onCancel,
    onMove,
}: MoveDialogProps): React.JSX.Element {
    const { entity, runtimeText, t } = useUiLanguage();
    const baseMap = getMap(mapId);
    const map = baseMap === undefined ? undefined : applyRackSettingsToMap(baseMap, rackSettings);
    const [rackId, setRackId] = useState(placement.rackId);
    const [startU, setStartU] = useState(placement.startU);
    const rack = map === undefined ? undefined : getRack(map, rackId);
    const component = getComponent(placement.componentId);
    const result =
        map === undefined
            ? { valid: false, reason: "Map unavailable." }
            : validatePlacement(map, placements, placement.componentId, rackId, startU, placement.id);

    return (
        <Modal
            title={t("moveComponent")}
            subtitle={`${
                placement.localName ??
                (component === undefined ? t("selectedEquipment") : entity("component", component.id, component.name))
            } · ${t("compatibleRack")}`}
            onClose={onCancel}
            footer={
                <>
                    <button className="button" type="button" onClick={onCancel}>
                        {t("cancel")}
                    </button>
                    <button
                        className="button button-primary"
                        type="button"
                        disabled={!result.valid}
                        onClick={() => onMove(rackId, startU)}
                    >
                        {t("moveTo")} U{startU}
                    </button>
                </>
            }
        >
            <div className="form-grid">
                <div className="form-field">
                    <span>{t("targetRack")}</span>
                    <SearchSelect
                        ariaLabel={t("targetRack")}
                        value={rackId}
                        options={(map?.racks ?? []).map((item) => ({
                            value: item.id,
                            label: entity("rack", item.id, item.name),
                            description: `${item.sizeU}U`,
                        }))}
                        onChange={setRackId}
                        searchPlaceholder={t("searchRacks")}
                    />
                </div>
                <label>
                    <span>{t("startRackUnit")}</span>
                    <input
                        type="number"
                        min="1"
                        max={rack?.sizeU ?? 42}
                        value={startU}
                        onChange={(event) => setStartU(Number(event.target.value))}
                    />
                </label>
            </div>
            <div className={`placement-feedback ${result.valid ? "is-valid" : "is-invalid"}`}>
                <strong>{result.valid ? t("destinationAvailable") : t("cannotMove")}</strong>
                <span>
                    {result.valid
                        ? `${component?.sizeU ?? 0}U · U${startU}–U${startU + (component?.sizeU ?? 1) - 1} · ${
                              rack === undefined ? t("thisRack") : entity("rack", rack.id, rack.name)
                          }`
                        : runtimeText(result.reason)}
                </span>
            </div>
        </Modal>
    );
}

export default function App(): React.JSX.Element {
    return (
        <LanguageContext.Provider value={{ mode: "technical", setMode: () => undefined }}>
            <PlannerApp />
        </LanguageContext.Provider>
    );
}

function PlannerApp(): React.JSX.Element {
    const [state, dispatch] = useReducer(plannerReducer, undefined, restoreInitialState);
    const [modal, setModal] = useState<ModalState>(null);
    const [capacityScope, setCapacityScope] = useState(DATACENTER_CAPACITY_SCOPE);
    const importRef = useRef<HTMLInputElement>(null);
    const savegameImportRef = useRef<HTMLInputElement>(null);
    const plan = state.plan;
    const map = useMemo(() => {
        if (plan === null) {
            return undefined;
        }
        const baseMap = getMap(plan.project.mapId);
        return baseMap === undefined ? undefined : applyRackSettingsToMap(baseMap, plan.rackSettings);
    }, [plan]);
    const selectedRack =
        map === undefined || state.selectedRackId === null ? undefined : getRack(map, state.selectedRackId);
    const capacityScopeOptions = useMemo(
        () => [
            {
                value: DATACENTER_CAPACITY_SCOPE,
                label: translate("technical", "entireDatacenter"),
                description: translate("technical", "allAvailabilityZones"),
            },
            ...(map?.zones.map((zone) => ({
                value: `${AVAILABILITY_ZONE_SCOPE_PREFIX}${zone.id}`,
                label: translateEntity("technical", "zone", zone.id, zone.name),
            })) ?? []),
        ],
        [map],
    );
    const displayedCapacity = useMemo(() => {
        if (map === undefined || plan === null) {
            return null;
        }
        if (!capacityScope.startsWith(AVAILABILITY_ZONE_SCOPE_PREFIX)) {
            return getDatacenterCapacity(map, plan.placements);
        }
        const zoneId = capacityScope.slice(AVAILABILITY_ZONE_SCOPE_PREFIX.length);
        if (!map.zones.some((zone) => zone.id === zoneId)) {
            return getDatacenterCapacity(map, plan.placements);
        }
        return getAvailabilityZoneCapacity(map, plan.placements, zoneId);
    }, [capacityScope, map, plan]);
    const selectedRackCapacity = useMemo(
        () => (selectedRack === undefined || plan === null ? null : getRackCapacity(selectedRack, plan.placements)),
        [plan, selectedRack],
    );
    const t = (key: Parameters<typeof translate>[1]): string => translate("technical", key);
    const entity = (kind: Parameters<typeof translateEntity>[1], id: string, fallback: string): string =>
        translateEntity("technical", kind, id, fallback);
    const runtimeText = (value: string | undefined): string => translateRuntimeText("technical", value);

    useEffect(() => {
        if (plan !== null) {
            saveLocalPlan(window.localStorage, plan);
        }
    }, [plan]);

    useEffect(() => {
        if (!capacityScopeOptions.some((option) => option.value === capacityScope)) {
            setCapacityScope(DATACENTER_CAPACITY_SCOPE);
        }
    }, [capacityScope, capacityScopeOptions]);

    useEffect(() => {
        function closeRackDesigner(event: KeyboardEvent): void {
            if (event.key !== "Escape" || modal !== null || state.view !== "rack") {
                return;
            }
            event.preventDefault();
            dispatch({ type: "set-view", view: "floorplan" });
        }

        window.addEventListener("keydown", closeRackDesigner);
        return () => window.removeEventListener("keydown", closeRackDesigner);
    }, [modal, state.view]);

    function requestImport(): void {
        importRef.current?.click();
    }

    function requestSavegameImport(): void {
        savegameImportRef.current?.click();
    }

    async function importFile(event: React.ChangeEvent<HTMLInputElement>): Promise<void> {
        const file = event.target.files?.[0];
        event.target.value = "";
        if (file === undefined) {
            return;
        }
        if (!file.name.toLowerCase().endsWith(".utp")) {
            dispatch({ type: "notice", notice: "Select a file with the .utp extension." });
            return;
        }
        const result = importPlanJson(await file.text());
        if (!result.ok || result.plan === undefined) {
            dispatch({ type: "notice", notice: result.error ?? "The plan could not be imported." });
            return;
        }
        dispatch({ type: "load-plan", plan: result.plan });
    }

    function selectSavegame(event: React.ChangeEvent<HTMLInputElement>): void {
        const file = event.target.files?.[0];
        event.target.value = "";
        if (file === undefined) {
            return;
        }
        if (!file.name.toLowerCase().endsWith(".save")) {
            dispatch({ type: "notice", notice: t("selectSavegameFile") });
            return;
        }
        setModal({ type: "import-savegame", file });
    }

    function exportCurrentPlan(): void {
        if (plan === null) {
            dispatch({ type: "notice", notice: "Create or import a plan first." });
            return;
        }
        const updatedAt = new Date().toISOString();
        downloadPlan(plan.project.name, JSON.stringify(serializePlan(plan, updatedAt), null, 2));
        dispatch({ type: "mark-exported", updatedAt });
    }

    function resetCurrentPlan(): void {
        dispatch({ type: "reset-plan" });
    }

    function renderWorkspace(): React.ReactNode {
        if (plan === null || map === undefined) {
            return (
                <EmptyWorkspace
                    onCreate={() => setModal({ type: "new-plan" })}
                    onImport={requestImport}
                    onImportSavegame={requestSavegameImport}
                    onImportApi={() => setModal({ type: "import-api" })}
                />
            );
        }
        const designerOpen = state.view === "rack" && selectedRack !== undefined;

        return (
            <div className={`floorplan-stage${designerOpen ? " has-rack-drawer" : ""}`}>
                <Floorplan
                    map={map}
                    placements={plan.placements}
                    rackSettings={plan.rackSettings}
                    selectedRackId={state.selectedRackId}
                    onSelectRack={(rackId, openDesigner) => {
                        dispatch({ type: "select-rack", rackId, openDesigner });
                    }}
                />
                {!designerOpen || selectedRack === undefined ? null : (
                    <>
                        {selectedRackCapacity === null ? null : (
                            <aside className="rack-stats-panel" aria-label={t("rackPerformance")}>
                                <CapacitySummary
                                    capacity={selectedRackCapacity}
                                    compact
                                    powerLabel={t("powerLoad")}
                                    powerTone="power"
                                    title={t("rackPerformance")}
                                />
                            </aside>
                        )}
                        <div className="rack-sidebar-stack">
                            <aside
                                className="rack-drawer"
                                aria-label={`${getRackDisplayName(
                                    selectedRack,
                                    plan.rackSettings,
                                    entity("rack", selectedRack.id, selectedRack.name),
                                )} ${t("rackDesigner")}`}
                            >
                                <RackDesigner
                                    map={map}
                                    rack={selectedRack}
                                    placements={plan.placements}
                                    rackSettings={plan.rackSettings}
                                    onPlace={(componentId, rackId, startU) =>
                                        dispatch({ type: "place", componentId, rackId, startU })
                                    }
                                    onPaste={(placements) => dispatch({ type: "place-many", placements })}
                                    onMove={(placementId, rackId, startU) =>
                                        dispatch({ type: "move", placementId, rackId, startU })
                                    }
                                    onMoveMany={(placements) => dispatch({ type: "move-many", placements })}
                                    onRename={(placement) => setModal({ type: "rename-placement", placement })}
                                    onEditRack={() => setModal({ type: "configure-rack", rackId: selectedRack.id })}
                                    onDelete={(placementId) => dispatch({ type: "delete", placementId })}
                                    onDeleteSelectionRequest={(placementIds) =>
                                        setModal({ type: "delete-placements", placementIds })
                                    }
                                />
                            </aside>
                            <aside className="placement-drawer" aria-label={t("catalogTitle")}>
                                <ComponentCatalog
                                    compact
                                    rack={selectedRack}
                                    mapId={plan.project.mapId}
                                    scenarioId={plan.project.scenarioId}
                                    onPlace={(component) => setModal({ type: "place", componentId: component.id })}
                                    onQuickPlace={(component) => {
                                        const startU = findHighestFreeStartU(
                                            map,
                                            plan.placements,
                                            component.id,
                                            selectedRack.id,
                                        );
                                        if (startU === null) {
                                            dispatch({ type: "notice", notice: t("quickPlacementUnavailable") });
                                            return;
                                        }
                                        dispatch({
                                            type: "place",
                                            componentId: component.id,
                                            rackId: selectedRack.id,
                                            startU,
                                        });
                                    }}
                                />
                            </aside>
                        </div>
                    </>
                )}
            </div>
        );
    }

    return (
        <div className="app-shell">
            <header className="topbar">
                <div className="topbar-context">
                    <div className="brand">
                        <img src="/assets/uptime_logo_flat.webp" alt="Uptime" />
                        <div>
                            <strong>{t("appName")}</strong>
                            <span className="brand-release">
                                <em>{t("beta")}</em>v{packageMetadata.version}
                            </span>
                        </div>
                    </div>
                    <div className="project-identity">
                        <span className="status-dot" />
                        <div>
                            <strong>{plan?.project.name ?? t("noPlanOpen")}</strong>
                            <span>{map === undefined ? t("createOrImport") : entity("map", map.id, map.name)}</span>
                        </div>
                    </div>
                    <div className="topbar-capacity">
                        {displayedCapacity === null ? null : (
                            <>
                                <SearchSelect
                                    ariaLabel={t("capacityScope")}
                                    compact
                                    value={capacityScope}
                                    options={capacityScopeOptions}
                                    onChange={setCapacityScope}
                                    searchPlaceholder={t("capacityScopeSearch")}
                                />
                                <CapacitySummary
                                    capacity={displayedCapacity}
                                    compact
                                    showTitle={false}
                                    title={t("datacenterCapacity")}
                                />
                            </>
                        )}
                    </div>
                </div>
                <nav className="topbar-menu" aria-label={t("planActions")}>
                    <button
                        className="button project-menu-button"
                        type="button"
                        onClick={() => setModal(plan === null ? { type: "new-plan" } : { type: "project" })}
                    >
                        <FolderIcon />
                        {plan === null ? t("newPlan") : t("project")}
                    </button>
                </nav>
            </header>
            <div className="app-body is-canvas-mode">
                <main className="workspace is-canvas">{renderWorkspace()}</main>
            </div>
            <input
                ref={importRef}
                className="sr-only"
                type="file"
                accept=".utp,application/json"
                onChange={(event) => {
                    void importFile(event);
                }}
            />
            <input
                ref={savegameImportRef}
                className="sr-only"
                type="file"
                accept=".save,application/octet-stream"
                onChange={selectSavegame}
            />
            {state.notice === null ? null : (
                <div className="toast" role="status" aria-live="polite">
                    <i className="toast-marker" aria-hidden="true" />
                    <span>{runtimeText(state.notice ?? undefined)}</span>
                    <button
                        type="button"
                        className="toast-dismiss"
                        onClick={() => dispatch({ type: "notice", notice: null })}
                        aria-label={t("dismissNotification")}
                    >
                        ×
                    </button>
                </div>
            )}
            {modal?.type === "project" && plan !== null && map !== undefined ? (
                <Modal
                    title={t("projectManagement")}
                    subtitle={t("projectManagementSubtitle")}
                    className="project-dialog"
                    onClose={() => setModal(null)}
                >
                    <ProjectView
                        planName={plan.project.name}
                        mapId={plan.project.mapId}
                        mapName={entity("map", map.id, map.name)}
                        scenarioId={plan.project.scenarioId}
                        createdAt={plan.project.createdAt}
                        updatedAt={plan.project.updatedAt}
                        rackCount={map.racks.length}
                        totalRackU={map.racks.reduce((total, rack) => total + rack.sizeU, 0)}
                        placementCount={plan.placements.length}
                        onNew={() => setModal({ type: "new-plan" })}
                        onImport={() => {
                            setModal(null);
                            requestImport();
                        }}
                        onImportSavegame={() => {
                            setModal(null);
                            requestSavegameImport();
                        }}
                        onImportApi={() => setModal({ type: "import-api" })}
                        onExport={exportCurrentPlan}
                        onRename={() => setModal({ type: "rename-plan" })}
                        onScenarioChange={(scenarioId) => dispatch({ type: "set-scenario", scenarioId })}
                        onReset={resetCurrentPlan}
                    />
                </Modal>
            ) : null}
            {modal?.type === "import-savegame" ? (
                <SavegameImportModal
                    file={modal.file}
                    replacesCurrentPlan={plan !== null}
                    onClose={() => setModal(null)}
                    onImport={(importedPlan) => {
                        dispatch({ type: "load-plan", plan: importedPlan, notice: t("savegameImported") });
                        setModal(null);
                    }}
                />
            ) : null}
            {modal?.type === "import-api" ? (
                <ApiImportModal
                    initialScenarioId={plan?.project.scenarioId}
                    replacesCurrentPlan={plan !== null}
                    onClose={() => setModal(null)}
                    onImport={(importedPlan) => {
                        dispatch({ type: "load-plan", plan: importedPlan, notice: t("apiImported") });
                        setModal(null);
                    }}
                />
            ) : null}
            {modal?.type === "new-plan" ? (
                <NewPlanModal
                    onClose={() => setModal(null)}
                    onCreate={(name, mapId, scenarioId) => {
                        dispatch({ type: "new-plan", name, mapId, scenarioId });
                        setModal(null);
                    }}
                />
            ) : null}
            {modal?.type === "rename-plan" && plan !== null ? (
                <TextModal
                    title={t("renamePlan")}
                    label={t("planName")}
                    initialValue={plan.project.name}
                    onClose={() => setModal(null)}
                    onSave={(name) => {
                        dispatch({ type: "rename-plan", name });
                        setModal(null);
                    }}
                />
            ) : null}
            {modal?.type === "rename-placement" ? (
                <TextModal
                    title={t("localComponentName")}
                    label={t("optionalLocalName")}
                    initialValue={modal.placement.localName ?? ""}
                    allowEmpty
                    onClose={() => setModal(null)}
                    onSave={(localName) => {
                        dispatch({ type: "rename-placement", placementId: modal.placement.id, localName });
                        setModal(null);
                    }}
                />
            ) : null}
            {modal?.type === "configure-rack" && map !== undefined && plan !== null ? (
                <RackSettingsModal
                    rack={getRack(map, modal.rackId)}
                    settings={findRackSettings(plan.rackSettings, modal.rackId)}
                    placements={plan.placements}
                    onClose={() => setModal(null)}
                    onSave={(rackId, name, showNameAsComponentPrefix, sizeU) => {
                        dispatch({ type: "configure-rack", rackId, name, showNameAsComponentPrefix, sizeU });
                        setModal(null);
                    }}
                />
            ) : null}
            {modal?.type === "move-placement" && plan !== null ? (
                <MoveDialog
                    mapId={plan.project.mapId}
                    placement={modal.placement}
                    placements={plan.placements}
                    rackSettings={plan.rackSettings}
                    onCancel={() => setModal(null)}
                    onMove={(rackId, startU) => {
                        dispatch({ type: "move", placementId: modal.placement.id, rackId, startU });
                        dispatch({ type: "select-rack", rackId, openDesigner: true });
                        setModal(null);
                    }}
                />
            ) : null}
            {modal?.type === "place" && plan !== null ? (
                <PlacementDialog
                    mapId={plan.project.mapId}
                    scenarioId={plan.project.scenarioId}
                    selectedRackId={state.selectedRackId}
                    placements={plan.placements}
                    rackSettings={plan.rackSettings}
                    {...(modal.componentId === undefined ? {} : { initialComponentId: modal.componentId })}
                    onCancel={() => setModal(null)}
                    onPlace={(componentId, rackId, startU, localName) => {
                        dispatch({
                            type: "place",
                            componentId,
                            rackId,
                            startU,
                            ...(localName === undefined ? {} : { localName }),
                        });
                        dispatch({ type: "select-rack", rackId, openDesigner: true });
                        setModal(null);
                    }}
                />
            ) : null}
            {modal?.type === "delete-placements" ? (
                <Modal
                    title={t("removeSelectedComponents")}
                    subtitle={t("removeSelectedComponentsHint")}
                    onClose={() => setModal(null)}
                    footer={
                        <>
                            <button className="button" type="button" onClick={() => setModal(null)}>
                                {t("cancel")}
                            </button>
                            <button
                                className="button button-danger"
                                type="button"
                                onClick={() => {
                                    dispatch({ type: "delete-many", placementIds: modal.placementIds });
                                    setModal(null);
                                }}
                            >
                                {t("confirmRemove")}
                            </button>
                        </>
                    }
                >
                    <div className="delete-confirmation">
                        <strong>{modal.placementIds.length}</strong>
                        <span>
                            {modal.placementIds.length === 1 ? t("component") : t("components")}{" "}
                            {t("selectedForRemoval")}
                        </span>
                    </div>
                </Modal>
            ) : null}
        </div>
    );
}

function NewPlanModal({
    onClose,
    onCreate,
}: {
    readonly onClose: () => void;
    readonly onCreate: (name: string, mapId: MapId, scenarioId: ScenarioId) => void;
}): React.JSX.Element {
    const { entity, t } = useUiLanguage();
    const [name, setName] = useState("");
    const [mapId, setMapId] = useState<MapId>("basement");
    const [scenarioId, setScenarioId] = useState<ScenarioId>(getDefaultScenarioId());
    const selectedMap = getMap(mapId);
    return (
        <Modal
            title={t("createNewPlan")}
            subtitle={t("newPlanSubtitle")}
            onClose={onClose}
            footer={
                <>
                    <button className="button" type="button" onClick={onClose}>
                        {t("cancel")}
                    </button>
                    <button
                        className="button button-primary"
                        type="button"
                        disabled={name.trim() === ""}
                        onClick={() => onCreate(name, mapId, scenarioId)}
                    >
                        {t("createPlan")}
                    </button>
                </>
            }
        >
            <div className="form-grid">
                <label>
                    <span>{t("planName")}</span>
                    <input
                        maxLength={80}
                        value={name}
                        onChange={(event) => setName(event.target.value)}
                        placeholder={t("planNamePlaceholder")}
                    />
                </label>
                <div className="form-field">
                    <span>{t("officialMap")}</span>
                    <SearchSelect
                        ariaLabel={t("officialMap")}
                        value={mapId}
                        options={getMaps().map((map) => ({
                            value: map.id,
                            label: entity("map", map.id, map.name),
                            description: `${map.racks.length} ${t("racks")} · ${map.racks.reduce((total, rack) => total + rack.sizeU, 0)}U`,
                        }))}
                        onChange={(value) => {
                            const nextMap = getMap(value);
                            if (nextMap !== undefined) {
                                setMapId(nextMap.id);
                            }
                        }}
                        searchPlaceholder={t("searchMaps")}
                    />
                    {selectedMap === undefined ? null : (
                        <div className="map-select-summary">
                            <span>
                                {selectedMap.racks.length} {t("fixedRackPositions")}
                            </span>
                            <span>
                                {selectedMap.racks.reduce((total, rack) => total + rack.sizeU, 0)} {t("totalRackUnits")}
                            </span>
                        </div>
                    )}
                </div>
                <div className="form-field">
                    <span>{t("scenario")}</span>
                    <SearchSelect
                        ariaLabel={t("scenario")}
                        value={scenarioId}
                        options={getScenarios(mapId).map((scenario) => ({
                            value: scenario.id,
                            label: entity("scenario", scenario.id, scenario.name),
                            description: t("scenarioAvailabilityHint"),
                        }))}
                        onChange={(value) => {
                            const scenario = getScenario(value);
                            if (scenario?.mapIds.includes(mapId)) {
                                setScenarioId(scenario.id);
                            }
                        }}
                        searchPlaceholder={t("searchScenarios")}
                    />
                </div>
            </div>
        </Modal>
    );
}

function TextModal({
    title,
    label,
    initialValue,
    allowEmpty = false,
    onClose,
    onSave,
}: {
    readonly title: string;
    readonly label: string;
    readonly initialValue: string;
    readonly allowEmpty?: boolean;
    readonly onClose: () => void;
    readonly onSave: (value: string) => void;
}): React.JSX.Element {
    const { t } = useUiLanguage();
    const [value, setValue] = useState(initialValue);
    const inputRef = useRef<HTMLInputElement>(null);
    const canSave = allowEmpty || value.trim() !== "";

    useEffect(() => {
        inputRef.current?.focus();
        inputRef.current?.select();
    }, []);

    return (
        <Modal
            title={title}
            onClose={onClose}
            footer={
                <>
                    <button className="button" type="button" onClick={onClose}>
                        {t("cancel")}
                    </button>
                    <button className="button button-primary" type="submit" form="text-modal-form" disabled={!canSave}>
                        {t("saveChanges")}
                    </button>
                </>
            }
        >
            <form
                id="text-modal-form"
                onSubmit={(event) => {
                    event.preventDefault();
                    if (canSave) {
                        onSave(value);
                    }
                }}
            >
                <div className="form-grid">
                    <label>
                        <span>{label}</span>
                        <input
                            ref={inputRef}
                            maxLength={80}
                            value={value}
                            onChange={(event) => setValue(event.target.value)}
                        />
                    </label>
                </div>
            </form>
        </Modal>
    );
}

function RackSettingsModal({
    rack,
    settings,
    placements,
    onClose,
    onSave,
}: {
    readonly rack: ReturnType<typeof getRack>;
    readonly settings: ReturnType<typeof findRackSettings>;
    readonly placements: readonly Placement[];
    readonly onClose: () => void;
    readonly onSave: (rackId: string, name: string, showNameAsComponentPrefix: boolean, sizeU: number) => void;
}): React.JSX.Element | null {
    const { entity, t } = useUiLanguage();
    const officialName = rack === undefined ? "" : entity("rack", rack.id, rack.name);
    const [name, setName] = useState(settings?.name ?? officialName);
    const [showPrefix, setShowPrefix] = useState(settings?.showNameAsComponentPrefix === true);
    const [sizeU, setSizeU] = useState<number>(settings?.sizeU ?? rack?.sizeU ?? 42);
    const inputRef = useRef<HTMLInputElement>(null);
    const sizeConfigurable = rack !== undefined && isRackSizeConfigurable(rack, settings);
    const sizeFits = rack !== undefined && rackContentsFitSize(rack, sizeU, placements);
    const canSave = rack !== undefined && name.trim() !== "" && sizeFits;

    useEffect(() => {
        inputRef.current?.focus();
        inputRef.current?.select();
    }, []);

    if (rack === undefined) {
        return null;
    }

    return (
        <Modal
            title={t("rackSettings")}
            subtitle={t("rackSettingsSubtitle")}
            onClose={onClose}
            footer={
                <>
                    <button className="button" type="button" onClick={onClose}>
                        {t("cancel")}
                    </button>
                    <button
                        className="button button-primary"
                        type="submit"
                        form="rack-settings-form"
                        disabled={!canSave}
                    >
                        {t("saveChanges")}
                    </button>
                </>
            }
        >
            <form
                id="rack-settings-form"
                className="form-grid"
                onSubmit={(event) => {
                    event.preventDefault();
                    if (canSave) {
                        onSave(rack.id, name, showPrefix, sizeU);
                    }
                }}
            >
                <label>
                    <span>{t("rackName")}</span>
                    <input
                        ref={inputRef}
                        maxLength={80}
                        value={name}
                        onChange={(event) => setName(event.target.value)}
                    />
                </label>
                <fieldset className="rack-size-field">
                    <legend>{t("rackHeight")}</legend>
                    {rack.type === "wall-4u" ? (
                        <div className="rack-size-readonly">
                            <strong>{rack.sizeU}U</strong>
                            <span>{t("rackHeightFixedHint")}</span>
                        </div>
                    ) : (
                        <>
                            <div className="rack-size-options">
                                {RACK_SIZE_OPTIONS.map((option) => (
                                    <label key={option} className={sizeU === option ? "is-selected" : undefined}>
                                        <input
                                            type="radio"
                                            name="rack-size"
                                            value={option}
                                            checked={sizeU === option}
                                            disabled={!sizeConfigurable}
                                            onChange={() => setSizeU(option)}
                                        />
                                        <strong>{option}U</strong>
                                        <span>{t("rackUnits")}</span>
                                    </label>
                                ))}
                            </div>
                            {!sizeConfigurable ? (
                                <small className="rack-size-hint">{t("rackHeightImportedHint")}</small>
                            ) : sizeFits ? (
                                <small className="rack-size-hint">{t("rackHeightHint")}</small>
                            ) : (
                                <small className="rack-size-hint is-error">{t("rackHeightContentConflict")}</small>
                            )}
                        </>
                    )}
                </fieldset>
                <label className="rack-prefix-option">
                    <input
                        type="checkbox"
                        checked={showPrefix}
                        onChange={(event) => setShowPrefix(event.target.checked)}
                    />
                    <span>
                        <strong>{t("showRackNameAsComponentPrefix")}</strong>
                        <small>{t("rackNamePrefixHint")}</small>
                    </span>
                </label>
            </form>
        </Modal>
    );
}
